import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { useTranslation } from '../utils/i18n';
import './KakaoCallbackPage.css';

/**
 * Kakao OAuth Callback Page
 *
 * This page handles the OAuth callback from Kakao after user authentication.
 * It exchanges the authorization code for user info and logs the user in.
 */
const KakaoCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [errorMessage, setErrorMessage] = useState('');
  const hasProcessedRef = useRef(false); // Prevent duplicate processing in StrictMode

  useEffect(() => {
    // Skip if already processed
    if (hasProcessedRef.current) {
      console.log('[Kakao Callback] Skipping duplicate call (already processed)');
      return;
    }

    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (error) {
      // Kakao returned an error
      console.error('[Kakao Callback] OAuth error:', error, errorDescription);
      setStatus('error');
      setErrorMessage(errorDescription || 'Kakao login cancelled or failed');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    if (!code) {
      console.error('[Kakao Callback] No authorization code received');
      setStatus('error');
      setErrorMessage('Invalid callback URL');
      setTimeout(() => navigate('/login'), 3000);
      return;
    }

    // Mark as processed before calling API
    hasProcessedRef.current = true;
    console.log('[Kakao Callback] Processing authorization code...');
    handleKakaoLogin(code);
  }, [searchParams, navigate]);

  const handleKakaoLogin = async (code) => {
    try {
      setStatus('processing');
      console.log('[Kakao Callback] Processing authorization code...');

      // Send code to backend
      const response = await api.post('/auth/kakao/callback', { code });

      console.log('[Kakao Callback] Login successful:', response.data);

      const { user } = response.data;
      const accessToken = user?.accessToken;

      if (!accessToken) {
        throw new Error('No access token received from server');
      }

      // Store token and user info
      localStorage.setItem('ctv_token', accessToken);
      localStorage.setItem('ctv_userInfo', JSON.stringify(user));
      localStorage.setItem('ctv_rememberMe', 'true'); // Remember Kakao logins

      setStatus('success');

      // Redirect to home after 1 second
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1000);
    } catch (error) {
      console.error('[Kakao Callback] Login failed:', error);
      setStatus('error');

      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        'Failed to login with Kakao';
      setErrorMessage(errorMsg);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login?error=kakao_failed', { replace: true });
      }, 3000);
    }
  };

  return (
    <div className="kakao-callback-page">
      <div className="kakao-callback-container">
        {status === 'processing' && (
          <>
            <div className="kakao-spinner"></div>
            <h2>{t('processingKakaoLogin') || 'Processing Kakao login...'}</h2>
            <p>{t('pleaseWait') || 'Please wait...'}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="kakao-success-icon">✓</div>
            <h2>{t('loginSuccessful') || 'Login successful!'}</h2>
            <p>{t('redirectingToHome') || 'Redirecting to home...'}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="kakao-error-icon">⚠️</div>
            <h2>{t('loginFailed') || 'Login failed'}</h2>
            <p className="error-message">{errorMessage}</p>
            <p className="redirect-message">
              {t('redirectingToLogin') || 'Redirecting to login page...'}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default KakaoCallbackPage;
